import React, { useState, useRef } from "react";
import { useTranslation } from "utilities/i18n";

/**
 * FloatingInput component.
 *
 * Renders a label / input pair that results in the placeholder "floating" (with animation) up above
 * the input when the input is focused or has content in it.
 *
 * Supports standard HTML input props (eg, "required", "maxLength", "minLength", "pattern", etc)
 *
 * Automatically displays error messages immediately below the input based on validity.
 *
 * @example
 *
 * <div className="col-12 col-sm-6">
 *   <FloatingInput
 *     classes={["a-class", "another-class"]}
 *     label="Device Name"
 *     value={formState.deviceName}
 *     onChange={updateForm}
 *     name="deviceName"
 *     required
 *     minLength={5}
 *     errorMessages={{
 *       badInput: "Some custom error message to display",
 *       patternMismatch: "Hey, use the right pattern!"
 *     }}
 *   />
 * </div>
 *
 */

type ValidatorFN = (validity?: ValidityState | null) => string | null;

// This typing allows you to pass only SOME of the validity message properties
type optionalValidityState = {
  [P in keyof ValidityState]?: string;
};

export interface FloatingInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  classes?: string[];
  validityFn?: ValidatorFN;
  errorMessages?: optionalValidityState;
}

const defaultValidator: (
  messages: optionalValidityState
) => ValidatorFN = messages => validity => {
  if (!validity) {
    return null;
  }

  const validityOptions: Array<keyof ValidityState> = [
    "badInput",
    "customError",
    "patternMismatch",
    "rangeOverflow",
    "rangeUnderflow",
    "stepMismatch",
    "tooLong",
    "tooShort",
    "typeMismatch",
    "valid",
    "valueMissing"
  ];

  return validityOptions
    .map(error => (validity[error] ? messages[error] : ""))
    .filter(message => "" !== message)
    .join(", ");
};

interface FloatingInputState {
  active: boolean;
  touched: boolean;
}

export const FloatingInput: React.FC<FloatingInputProps> = props => {
  if (!props.id && !props.name) {
    throw new Error("expected id or name but none present");
  }

  const { t } = useTranslation();
  const inputRef: React.RefObject<HTMLInputElement> = useRef(null);
  const defaultState: FloatingInputState = {
    active: !!props.value,
    touched: false
  };

  const [state, setState] = useState(defaultState);

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setState({ ...state, active: true });
    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target;
    const active = target.value.length > 0;

    setState({ active: active, touched: true });

    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  let {
    id,
    classes = [],
    label = "",
    name,
    type = "text",
    validityFn,
    errorMessages,
    ...otherProps
  } = props;
  const { active, touched } = state;

  const baseInputClasses = ["form-control"];

  const wrapClasses = ["floating-label-input"];
  if (active) {
    wrapClasses.push("active");
  }

  if (touched) {
    wrapClasses.push("touched");

    if (!inputRef.current?.validity.valid) {
      wrapClasses.push("has-error");
    }
  }

  if (!id) {
    id = `${name}-${label}`;
  }

  const defaultMessages: Record<keyof ValidityState, string> = {
    badInput: t("FORM_VALIDATE_BAD_VALUE"), // "Please enter a valid value.",
    customError: t("FORM_VALIDATE_CUSTOM_ERROR"), // "This is a custom error message.",
    patternMismatch: t("FORM_VALIDATE_PATTERN_MISMATCH", {
      pattern: inputRef.current?.pattern
    }), // `Please enter a value that matches the pattern ${inputRef.current?.pattern}.`,
    rangeOverflow: t("FORM_VALIDATE_RANGE_OVERFLOW", {
      max: inputRef.current?.max
    }), // `The number entered should be less than ${inputRef.current?.max}.`,
    rangeUnderflow: t("FORM_VALIDATE_RANGE_UNDERFLOW", {
      min: inputRef.current?.min
    }), // `The number entered should be greater than ${inputRef.current?.min}.`,
    stepMismatch: t("FORM_VALIDATE_STEP_MISMATCH", {
      step: inputRef.current?.step
    }), // `Your number must be in increments of ${inputRef.current?.step}.`,
    tooLong: t("FORM_VALIDATE_TOO_LONG", {
      minLength: inputRef.current?.maxLength
    }), // `Please enter at least ${inputRef.current?.minLength} characters.`,
    tooShort: t("FORM_VALIDATE_TOO_SHORT", {
      maxLength: inputRef.current?.minLength
    }), // `Please enter no more than ${inputRef.current?.maxLength} characters.`,
    typeMismatch: t("FORM_VALIDATE_TYPE_MISMATCH"), // `Whatcha  doing?`,
    valid: t("FORM_VALIDATE_VALID"), // "This should not show.",
    valueMissing: t("FORM_VALIDATE_VALUE_MISSING") // "Please input a value."
  };

  const validatorForElement =
    validityFn ?? defaultValidator(errorMessages ?? defaultMessages);

  const errorMessage: string | null = validatorForElement(
    inputRef.current?.validity
  );

  return (
    <div className={wrapClasses.join(" ")}>
      <div className="label-container">
        <label className="floating-label" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          className={[...baseInputClasses, ...classes].join(" ")}
          aria-label={label}
          onBlur={onBlur}
          onFocus={onFocus}
          name={name}
          ref={inputRef}
          type={type}
          {...otherProps}
        />
      </div>
      {touched && errorMessage !== null ? (
        <p className="error-message">{errorMessage}</p>
      ) : null}
    </div>
  );
};

export default FloatingInput;
